import { Component, ElementRef, OnInit, Optional, ViewChild } from '@angular/core';
import { CommonModule, DatePipe, DecimalPipe } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FormControl } from '@angular/forms';
import * as ExcelJS from 'exceljs';
import { MatDialogModule, MatDialogRef, MatDialog } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatTableModule } from '@angular/material/table';
import { MatSortModule, Sort } from '@angular/material/sort';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MaterialsQaqcDto, MaterialsQaqcSearchParams, ToolsService, AddMaterialRequestQaqc } from '../../services/tools.service';
import { CisProSearchComponent } from '../../project-details/cispro-search/cispro-search.component';
import { CisProSearchResult } from '../../services/cispro-search.service';
import { debounceTime, distinctUntilChanged, switchMap, of } from 'rxjs';
import { EditMaterialDialogComponent, EditMaterialDialogData } from './edit-material-dialog/edit-material-dialog.component';
import { PrintService } from '../../shared/print/print-dialog.component';
import { LookupService, MaterialLookup } from '../../services/lookup.service';

@Component({
  selector: 'app-materials-qaqc',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatInputModule,
    MatFormFieldModule,
    MatTableModule,
    MatSortModule,
    MatProgressSpinnerModule,
    MatAutocompleteModule,
    MatSelectModule,
    MatCheckboxModule,
    MatDatepickerModule,
    MatNativeDateModule,
    CisProSearchComponent
  ],
  providers: [DatePipe, DecimalPipe],
  templateUrl: './materials-qaqc.component.html',
  styleUrls: ['./materials-qaqc.component.css'],
})
export class MaterialsQAQC implements OnInit {
  // Search fields
  materialName = '';
  cisProContainer = '';
  sampleRefNum = '';
  rigName = '';

  // Add Material autocomplete
  addMaterialControl = new FormControl('');
  filteredMaterialNames: MaterialLookup[] = [];
  isLoadingAutocomplete = false;

  // Add Material Form - shows when material is selected
  materialSelected = false;
  selectedMaterial: MaterialsQaqcDto | null = null;

  // Row selection for grid
  selectedRow: MaterialsQaqcDto | null = null;

  // Add Material Form Fields
  addMaterial = {
    name: '',
    materialId: 0,
    type: 0,
    cisProContainerId: '',
    cisProLocation: '',
    specificGravity: null as number | null,
    ph: null as number | null,
    chlorides: null as number | null,
    comments: '',
    sampleRefNum: '',
    recvAmount: null as number | null,
    selectedContainerWeight: '', // Empty by default
    contWeightAmount: 0,
    otherWeight: null as number | null,
    minAmount: null as number | null,
    rigName: '',
    recvDate: new Date().toISOString().split('T')[0], // yyyy-MM-dd format for date input
    trackSample: true,
    notifyAtReorder: true
  };

  // Formatted display values for Add Material
  addMaterialDisplay = {
    specificGravity: '',
    ph: '',
    chlorides: '',
    recvAmount: '',
    minAmount: ''
  };

  // Container weight options
  containerWeights = [
    { id: 1, name: '5 Gal', weight: 1245 },
    { id: 2, name: '3 Gal', weight: 1064 },
    { id: 3, name: '2 Gal', weight: 550 },
    { id: 4, name: '1 Gal', weight: 300 },
    { id: 5, name: 'Gal Jug', weight: 110 },
    { id: 6, name: '1 qt (H)', weight: 100 },
    { id: 7, name: '1 qt (s)', weight: 100 },
    { id: 8, name: '1 qt (RD)', weight: 75 },
    { id: 9, name: '1 pt', weight: 70 },
    { id: 10, name: '1 pt (S)', weight: 55 },
    { id: 11, name: '.50 pt', weight: 40 },
    { id: 12, name: '.25 pt', weight: 25 },
    { id: 13, name: 'Other', weight: 0 }
  ];

  // Track previous container weight for calculations
  private previousContWeightAmount: number = 0; // Default to '1 Gal' weight

  // Liquid material types
  private readonly liquidTypes = [2, 4, 9, 10];

  get isLiquidMaterial(): boolean {
    return this.liquidTypes.includes(this.addMaterial.type);
  }

  get showOtherInput(): boolean {
    return this.addMaterial.selectedContainerWeight === 'Other';
  }

  // Check if Add button should be enabled
  // Enabled only when CisPRO Container ID and Location are filled
  get canAddMaterial(): boolean {
    return !!(this.addMaterial.cisProContainerId && this.addMaterial.cisProContainerId.trim() !== '' &&
      this.addMaterial.cisProLocation && this.addMaterial.cisProLocation.trim() !== '');
  }

  // Data
  materials: MaterialsQaqcDto[] = [];
  filteredMaterials: MaterialsQaqcDto[] = [];
  isLoading = false;
  errorMessage = '';

  // Table configuration
  displayedColumns: string[] = [
    'name',
    'type',
    'cisProContainerId',
    'cisProLocation',
    'specificGravity',
    'ph',
    'chlorides',
    'sampleRefNum',
    'recvAmount',
    'currentAmount',
    'minAmount',
    'recvDate',
    'currentDate',
    'rigName',
    'lastUsedBy',
    'lastUsedIn',
    'notifyAtReorder',
    'trackSample'
  ];

  // Check if component is opened as a dialog or as a routed page
  isDialog = false;

  /**
   * Format number to fixed decimal places
   */
  private formatDecimal(value: number | null, decimals: number): string {
    if (value === null || value === undefined) return '';
    return value.toFixed(decimals);
  }

  /**
   * Parse string to number, return null if invalid
   */
  private parseNumber(value: string): number | null {
    if (!value || value.trim() === '') return null;
    const num = parseFloat(value);
    return isNaN(num) ? null : num;
  }

  /**
   * Handle specific gravity input change for Add Material
   */
  onAddSpecificGravityChange(value: string): void {
    this.addMaterial.specificGravity = this.parseNumber(value);
  }

  /**
   * Handle pH input change for Add Material
   */
  onAddPhChange(value: string): void {
    this.addMaterial.ph = this.parseNumber(value);
  }

  /**
   * Handle chlorides input change for Add Material
   */
  onAddChloridesChange(value: string): void {
    this.addMaterial.chlorides = this.parseNumber(value);
  }

  /**
   * Handle received amount input change for Add Material
   */
  onAddRecvAmountInputChange(value: string): void {
    this.addMaterial.recvAmount = this.parseNumber(value);
    this.onRecvAmountChange();
  }

  /**
   * Handle minimum amount input change for Add Material
   */
  onAddMinAmountChange(value: string): void {
    this.addMaterial.minAmount = this.parseNumber(value);
  }

  constructor(
    private materialsQaqcService: ToolsService,
    private dialog: MatDialog,
    private printService: PrintService,
    private datePipe: DatePipe,
    private decimalPipe: DecimalPipe,
    private lookupService: LookupService,
    @Optional() public dialogRef: MatDialogRef<MaterialsQAQC>
  ) {
    this.isDialog = !!dialogRef;
  }

  ngOnInit(): void {
    // Initially load all materials (with empty search params)
    this.searchMaterials();

    // Setup autocomplete for Add Material
    this.setupAddMaterialAutocomplete();
  }

  /**
   * Setup autocomplete for Add Material textbox
   */
  setupAddMaterialAutocomplete(): void {
    this.addMaterialControl.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((value) => {
          if (!value || value.length < 2) {
            this.filteredMaterialNames = [];
            return of({ data: [] as MaterialLookup[], searchValue: '' });
          }
          this.isLoadingAutocomplete = true;
          // Return both the data and the search value for filtering
          return this.lookupService.getMaterials().pipe(
            switchMap(data => of({ data, searchValue: value }))
          );
        })
      )
      .subscribe({
        next: (result) => {
          if (result.searchValue) {
            // Filter materials based on user input (case-insensitive)
            const searchLower = result.searchValue.toLowerCase();
            this.filteredMaterialNames = result.data.filter(material =>
              material.name.toLowerCase().includes(searchLower)
            );
          } else {
            this.filteredMaterialNames = [];
          }
          this.isLoadingAutocomplete = false;
        },
        error: () => {
          this.filteredMaterialNames = [];
          this.isLoadingAutocomplete = false;
        }
      });
  }

  /**
   * Handle material selection from autocomplete
   */
  onMaterialSelected(material: MaterialLookup): void {
    if (!material || typeof material === 'string') {
      // Handle case where material is a string (shouldn't happen but safety check)
      return;
    }

    this.selectedMaterial = null;
    this.materialSelected = true;

    // Populate add material form with selected material details
    this.addMaterial.name = material.name;
    this.addMaterial.materialId = material.id;
    this.addMaterial.type = material.materialTypeId;
    this.addMaterial.specificGravity = null;
    this.addMaterial.ph = null;
    this.addMaterial.chlorides = null;
    this.addMaterial.cisProContainerId = '';
    this.addMaterial.cisProLocation = '';
    this.addMaterial.comments = '';
    this.addMaterial.sampleRefNum = '';
    this.addMaterial.recvAmount = null;
    this.addMaterial.selectedContainerWeight = ''; // Empty by default
    this.addMaterial.contWeightAmount = 0;
    this.addMaterial.otherWeight = null;
    this.addMaterial.minAmount = null;
    this.addMaterial.rigName = '';
    this.addMaterial.recvDate = new Date().toISOString().split('T')[0];
    this.addMaterial.trackSample = true;
    this.addMaterial.notifyAtReorder = true;
    // Reset previous container weight
    this.previousContWeightAmount = 0;

    // Reset display values
    this.addMaterialDisplay = {
      specificGravity: '',
      ph: '',
      chlorides: '',
      recvAmount: '',
      minAmount: ''
    };

    // Bubble matching records to the top
    this.filteredMaterials = [
      ...this.filteredMaterials.filter(m => m.name === material.name),
      ...this.filteredMaterials.filter(m => m.name !== material.name)
    ];
  }

  /**
   * Display function for autocomplete - shows only material name
   */
  displayMaterialName(material: MaterialLookup | string): string {
    if (!material) return '';
    if (typeof material === 'string') return material;
    return material.name;
  }

  onMaterialInput(value: string): void {
    this.materialSelected = false;
    const q = value.trim().toLowerCase();
    if (!q) {
      this.filteredMaterialNames = [];
      this.isLoadingAutocomplete = false;
      return;
    }
    this.filteredMaterialNames = this.filteredMaterialNames.filter(m => m.name.toLowerCase().includes(q));
    this.isLoadingAutocomplete = this.filteredMaterialNames.length > 0;
  }
  /**
   * Clear selected material and reset form
   */
  clearMaterial(): void {
    this.materialSelected = false;
    this.selectedMaterial = null;
    this.addMaterialControl.setValue('');
    this.addMaterial = {
      name: '',
      materialId: 0,
      type: 0,
      cisProContainerId: '',
      cisProLocation: '',
      specificGravity: null,
      ph: null,
      chlorides: null,
      comments: '',
      sampleRefNum: '',
      recvAmount: null,
      selectedContainerWeight: '', // Empty by default
      contWeightAmount: 0,
      otherWeight: null,
      minAmount: null,
      rigName: '',
      recvDate: new Date().toISOString().split('T')[0],
      trackSample: false,
      notifyAtReorder: true
    };
    // Reset display values
    this.addMaterialDisplay = {
      specificGravity: '',
      ph: '',
      chlorides: '',
      recvAmount: '',
      minAmount: ''
    };
    // Reset previous container weight to default
    this.previousContWeightAmount = 0;
  }

  /**
   * Handle container weight change
   * Based on ToolsRepository.cs logic for ContWeightAmount changes
   */
  onContainerWeightChange(): void {
    const selectedWeight = this.containerWeights.find(cw => cw.name === this.addMaterial.selectedContainerWeight);

    if (!selectedWeight) return;

    const newContWeightAmount = selectedWeight.weight;

    // If "Other" is selected, clear the container weight amount and let user enter otherWeight
    if (this.showOtherInput) {
      this.addMaterial.contWeightAmount = 0;
      this.addMaterial.otherWeight = 0;
      return;
    }

    // Clear otherWeight when switching from "Other" to a specific container
    this.addMaterial.otherWeight = null;

    // Adjust RecvAmount based on container weight change (matching backend logic)
    if (this.addMaterial.recvAmount !== null && this.addMaterial.recvAmount > 0) {
      // Add back the previous container weight
      this.addMaterial.recvAmount = this.addMaterial.recvAmount + this.previousContWeightAmount;
      // Subtract the new container weight
      this.addMaterial.recvAmount = this.addMaterial.recvAmount - newContWeightAmount;
      // Recalculate MinAmount as 1/3 of RecvAmount (3 decimal places)
      this.addMaterial.minAmount = parseFloat((this.addMaterial.recvAmount / 3).toFixed(3));
    }

    // Update container weight amount and previous for next change
    this.addMaterial.contWeightAmount = newContWeightAmount;
    this.previousContWeightAmount = newContWeightAmount;

    // Update display values
    this.addMaterialDisplay.recvAmount = this.formatDecimal(this.addMaterial.recvAmount, 3);
    this.addMaterialDisplay.minAmount = this.formatDecimal(this.addMaterial.minAmount, 3);
  }

  /**
   * Handle other weight change
   */
  onOtherWeightChange(): void {
     if (
      this.addMaterial.recvAmount !== null &&
      this.addMaterial.otherWeight !== null
    ) {

      this.addMaterial.recvAmount =
        this.addMaterial.recvAmount - this.addMaterial.otherWeight;

      this.addMaterial.minAmount =
        parseFloat((this.addMaterial.recvAmount / 3).toFixed(3));

      this.addMaterialDisplay.recvAmount =
        this.formatDecimal(this.addMaterial.recvAmount, 3);

      this.addMaterialDisplay.minAmount =
        this.formatDecimal(this.addMaterial.minAmount, 3);
    }
  }

  /**
   * Handle received amount change
   */
  onRecvAmountChange(): void {
    if (this.addMaterial.recvAmount !== null && this.addMaterial.recvAmount > 0) {
      let adjustedAmount = this.addMaterial.recvAmount;

      // Subtract OtherWeight if it exists
      if (this.addMaterial.otherWeight !== null && this.addMaterial.otherWeight > 0) {
        adjustedAmount = this.addMaterial.recvAmount - this.addMaterial.otherWeight;
      }
      // Otherwise subtract ContWeightAmount if it exists
      else if (!this.showOtherInput) {
        if (this.addMaterial.contWeightAmount > 0) {
          adjustedAmount = this.addMaterial.recvAmount - this.addMaterial.contWeightAmount;
        }
      }

      // Recalculate MinAmount as 1/3 of adjusted amount (3 decimal places)
      this.addMaterial.minAmount = parseFloat((adjustedAmount / 3).toFixed(3));
      this.addMaterialDisplay.minAmount = this.formatDecimal(this.addMaterial.minAmount, 3);
    }
  }

  /**
   * Add new material
   */
  addNewMaterial(): void {
    if (!this.canAddMaterial) return;

    // Get the container weight ID from the selected name
    const selectedWeight = this.containerWeights.find(cw => cw.name === this.addMaterial.selectedContainerWeight);

    const request: AddMaterialRequestQaqc = {
      materialId: this.addMaterial.materialId,
      cisProContainerId: this.addMaterial.cisProContainerId,
      cisProLocation: this.addMaterial.cisProLocation,
      specificGravity: this.addMaterial.specificGravity,
      ph: this.addMaterial.ph,
      chlorides: this.addMaterial.chlorides,
      comments: this.addMaterial.comments,
      sampleRefNum: this.addMaterial.sampleRefNum,
      recvAmount: this.addMaterial.recvAmount,
      containerWeightId: selectedWeight?.id ?? null,
      contWeightAmount: this.addMaterial.contWeightAmount,
      otherWeight: this.showOtherInput ? this.addMaterial.otherWeight : null,
      minAmount: this.addMaterial.minAmount,
      rigName: this.addMaterial.rigName,
      recvDate: this.addMaterial.recvDate || null,
      trackSample: this.addMaterial.trackSample,
      notifyAtReorder: this.addMaterial.notifyAtReorder
    };

    this.isLoading = true;
    this.materialsQaqcService.addMaterialQaqc(request).subscribe({
      next: () => {
        this.clearMaterial();
        this.searchMaterials();
      },
      error: (error) => {
        console.error('Error adding material:', error);
        this.errorMessage = error?.error?.detail || error?.error?.title || 'Failed to add material. Please try again.';
        this.isLoading = false;
      }
    });
  }

  /**
   * Clear CisPRO Container field
   */
  clearCisProContainer(): void {
    this.addMaterial.cisProContainerId = '';
    this.addMaterial.cisProLocation = '';
  }

  /**
   * Open CisPRO Search dialog
   */
  openCisProSearch(): void {
    const dialogRef = this.dialog.open(CisProSearchComponent, {
      width: '90%',
      maxWidth: '1300px',
      maxHeight: '85vh',
      panelClass: 'cispro-search-dialog'
    });

    dialogRef.afterClosed().subscribe((result: CisProSearchResult | undefined) => {
      if (result) {
        this.addMaterial.cisProContainerId = result.barcodeId || '';
        this.addMaterial.cisProLocation = result.location || '';
      }
    });
  }

  /**
   * Search/filter materials based on search criteria
   */
  searchMaterials(): void {
    this.isLoading = true;
    this.errorMessage = '';

    const searchParams: MaterialsQaqcSearchParams = {
      materialName: this.materialName,
      barcodeId: this.cisProContainer,
      sampleRefNum: this.sampleRefNum,
      rigName: this.rigName
    };

    this.materialsQaqcService.searchMaterialsQaqc(searchParams).subscribe({
      next: (data) => {
        this.materials = data;
        this.filteredMaterials = data;
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading materials:', error);
        this.errorMessage = 'Failed to load materials. Please try again.';
        this.isLoading = false;
      }
    });
  }

  /**
   * Handle row click to select/highlight a row
   */
  onRowClick(row: MaterialsQaqcDto): void {
    this.selectedRow = row;
  }

  /**
   * Open edit dialog when a row is double-clicked and highlight the row
   */
  onRowDoubleClick(material: MaterialsQaqcDto): void {
    this.selectedRow = material;

    const dialogData: EditMaterialDialogData = {
      material: material
    };

    const dialogRef = this.dialog.open(EditMaterialDialogComponent, {
      width: '600px',
      disableClose: true,
      data: dialogData,
      panelClass: 'edit-material-dialog-panel'
    });

    dialogRef.afterClosed().subscribe((result) => {
        this.selectedRow = null;
      if (result === true) {
        // Refresh the materials list after successful update
        this.searchMaterials();
      }
    });
  }

  /**
   * Export materials data (placeholder for future implementation)
   */
  exportData(): void {
    console.log('Export functionality to be implemented');
    // TODO: Implement export to Excel/PDF
    const searchParams: MaterialsQaqcSearchParams = {
      materialName: this.materialName,
      barcodeId: this.cisProContainer,
      sampleRefNum: this.sampleRefNum,
      rigName: this.rigName
    };
    this.materialsQaqcService.searchMaterialsQaqc(searchParams).subscribe({
      next: (data) => {
        const headerRow = [
          'Sample Ref#',
          'Material Name',
          'CisPro Container',
          'CisPro Location',
          'Total Amount Recv.',
          'Current Amount',
          'Minimum Amount to Notify',
          'Sample Received Date',
          'Current Date',
          'Rig Name',
          'Last Used By (CAI)',
          'Last Used In(Slurry Weight Up)'
        ];
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Materials QAQC');
        // Freeze first 2 columns (Sample Ref# and Material Name)
        worksheet.views = [{ state: 'frozen', xSplit: 2, ySplit: 1, activeCell: 'C2' }];
        worksheet.addRow(headerRow);
        worksheet.getRow(1).font = { bold: true, size: 12, color: { argb: '000000' } };
        worksheet.getRow(1).alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
        worksheet.getRow(1).height = 30;
        worksheet.getRow(1).eachCell((cell) => {
          const colNum = typeof cell.col === 'string' ? parseInt(cell.col) : cell.col;
          if (colNum <= 4) {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FCE4D6' } };
          }
          cell.border = {
            top: { style: 'thin' }, left: { style: 'thin' },
            bottom: { style: 'thin' }, right: { style: 'thin' }
          };
        });
        const parseDate = (val: string | null | undefined): Date | string => {
          if (!val) return '';
          const d = new Date(val);
          return isNaN(d.getTime()) ? val : d;
        };
        data.forEach((item) => {
          const addedRow = worksheet.addRow([
            item.sampleRefNum,                  // col 1 - text
            item.name,                          // col 2 - text
            item.cisProContainerId,             // col 3 - text
            item.cisProLocation,                // col 4 - text
            item.recvAmount ?? null,            // col 5 - number
            item.currentAmount ?? null,         // col 6 - number
            item.minAmount ?? null,             // col 7 - number
            parseDate(item.recvDate),           // col 8 - date
            parseDate(item.currentDate),        // col 9 - date
            item.rigName,                       // col 10 - text
            item.updateUser,                    // col 11 - text
            item.surryName                      // col 12 - text
          ]);
          addedRow.eachCell({ includeEmpty: true }, (cell) => {
            const colNum = typeof cell.col === 'string' ? parseInt(cell.col) : cell.col;
            cell.alignment = { horizontal: 'center', vertical: 'middle' };
            cell.border = {
              top: { style: 'thin' }, left: { style: 'thin' },
              bottom: { style: 'thin' }, right: { style: 'thin' }
            };
            // Color only first 4 columns
            if (colNum <= 4) {
              cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FCE4D6' } };
            }
            // Number format for amount columns (5,6,7)
            if (colNum >= 5 && colNum <= 7) {
              cell.numFmt = '#,##0.00';
            }
            // Date format for date columns (8,9)
            if (colNum === 8 || colNum === 9) {
              cell.numFmt = 'mm/dd/yyyy';
            }
          });
        });
        worksheet.columns.forEach((column) => {
          if (!column) return;
          let maxLength = 10;
          column.eachCell?.({ includeEmpty: true }, (cell) => {
            const cellValue = cell.value ? cell.value.toString() : '';
            maxLength = Math.max(maxLength, cellValue.length);
          });
          column.width = maxLength + 5;
        });
        workbook.xlsx.writeBuffer().then((buffer) => {
          const blob = new Blob([buffer], { type: 'application/octet-stream' });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = 'Materials Inventory.xlsx';
          a.click();
          window.URL.revokeObjectURL(url);
        });
      },
      error: (error) => {
        console.error('Error exporting materials:', error);
        this.errorMessage = 'Failed to export materials. Please try again.';
      }
    });
  }

  /**
   * Generate PDF - Opens Inventory Report preview in a new window
   */

  @ViewChild('inventoryReportContent')
  inventoryReportContent!: ElementRef<HTMLElement>;

  inventoryReportHtml = '';
  //showInventoryPreview = false;
  generatePdf(): void {
    this.inventoryReportHtml = this.buildInventoryReportHtml();

    setTimeout(() => {
      const contentEl = this.inventoryReportContent.nativeElement;

      this.printService.print(contentEl, {
        // Open the popup at full screen size so the OS print dialog (which is
        // hosted inside the popup) gets the whole viewport for its preview,
        // and the popup's own blank body doesn't peek out around the dialog.
        windowWidth: window.screen.availWidth,
        windowHeight: window.screen.availHeight,
        bodyPadding: '6mm',
        colorMap: {
          'bg-yellow': { bg: '#FAAB18' },
          'bg-red': { bg: '#E21836' }
        },
        decorators: [
          {
            selector: '.inventory-table',
            style: `
                        width: 100% !important;
                        max-width: 100% !important;
                        table-layout: fixed !important;
                        border-collapse: collapse !important;
                        font-family: GothamNarrowBook;
                        font-size: 10px !important;
                        word-break: break-word !important;
                        overflow-wrap: anywhere !important;
                    `
          },
          {
            selector: '.inventory-table th',
            style: `
                        background-color: #E21836 !important;
                        color: #000 !important;
                        border: 1px solid #000 !important;
                        padding: 2px 4px !important;
                        text-align: center !important;
                        font-weight: bold !important;
                        word-break: break-word !important;
                        overflow-wrap: anywhere !important;
                        -webkit-print-color-adjust: exact !important;
                        print-color-adjust: exact !important;
                    `
          },
          {
            selector: '.inventory-table .title-row th',
            style: `
                        background-color: #FAAB18 !important;
                        border: 1px solid #000 !important;
                        font-weight: bold !important;
                        text-align: center !important;
                        -webkit-print-color-adjust: exact !important;
                        print-color-adjust: exact !important;
                    `
          },
          {
            selector: '.inventory-table td',
            style: `
                        border: 1px solid #000 !important;
                        padding: 1px 3px !important;
                        background-color: #fff !important;
                        word-break: break-word !important;
                        overflow-wrap: anywhere !important;
                        white-space: normal !important;
                    `
          },
          {
            selector: '.number',
            style: 'text-align: right !important;'
          },
          {
            selector: '.center',
            style: 'text-align: center !important;'
          },
          {
            selector: '.table-container',
            style: `
                        border: 1px solid #000 !important;
                        width: 100% !important;
                        max-width: 100% !important;
                        overflow: visible !important;
                        page-break-inside: auto !important;
                        break-inside: auto !important;
                    `
          },
          {
            selector: 'thead',
            style: `
                      display: table-row-group !important;
                   `
          }
        ],
        fallbackBodyClass: 'inventory-report-printing'
      });
    });
  }
  private buildInventoryReportHtml(): string {
    const rows = this.filteredMaterials.map(material => `
        <tr>
            <td>${material.sampleRefNum || ''}</td>
            <td>${material.name || ''}</td>
            <td>${material.cisProContainerId || ''}</td>
            <td>${material.cisProLocation || ''}</td>
            <td class="number">${this.formatAmountForPrint(material.recvAmount)}</td>
            <td class="number">${this.formatAmountForPrint(material.currentAmount)}</td>
            <td class="number">${this.formatAmountForPrint(material.minAmount)}</td>
            <td class="center">${this.formatDateForPrint(material.recvDate)}</td>
            <td class="center">${this.formatDateForPrint(material.currentDate)}</td>
            <td>${material.rigName || ''}</td>
        </tr>
    `).join('');

    return `
        <div class="table-container">
            <table class="inventory-table">
                <colgroup>
                    <col style="width: 8%" />
                    <col style="width: 12%" />
                    <col style="width: 11%" />
                    <col style="width: 15%" />
                    <col style="width: 9%" />
                    <col style="width: 9%" />
                    <col style="width: 9%" />
                    <col style="width: 8%" />
                    <col style="width: 8%" />
                    <col style="width: 11%" />
                </colgroup>
                <thead>
                    <tr class="title-row">
                        <th colspan="10">
                            Material QAQC Inventory Report
                        </th>
                    </tr>
                    <tr>
                        <th>SampleRefNum</th>
                        <th>Name</th>
                        <th>CisProContainerId</th>
                        <th>CisProLocation</th>
                        <th>RecvAmount</th>
                        <th>CurrentAmount</th>
                        <th>MinAmount</th>
                        <th>RecvDate</th>
                        <th>CurrentDate</th>
                        <th>RigName</th>
                    </tr>
                </thead>
                <tbody>
                    ${rows}
                </tbody>
            </table>
        </div>
    `;
  }
  /**
   * Sort data
   */
  sortData(sort: Sort): void {
    const data = this.materials.slice();
    if (!sort.active || sort.direction === '') {
      this.filteredMaterials = data;
      return;
    }

    this.filteredMaterials = data.sort((a, b) => {
      const isAsc = sort.direction === 'asc';
      switch (sort.active) {
        case 'name':
          return this.compare(a.name, b.name, isAsc);
        case 'type':
          return this.compare(a.type, b.type, isAsc);
        case 'cisProContainerId':
          return this.compare(a.cisProContainerId, b.cisProContainerId, isAsc);
        case 'cisProLocation':
          return this.compare(a.cisProLocation, b.cisProLocation, isAsc);
        case 'specificGravity':
          return this.compare(a.specificGravity || 0, b.specificGravity || 0, isAsc);
        case 'ph':
          return this.compare(a.ph || 0, b.ph || 0, isAsc);
        case 'chlorides':
          return this.compare(a.chlorides || 0, b.chlorides || 0, isAsc);
        case 'sampleRefNum':
          return this.compare(a.sampleRefNum, b.sampleRefNum, isAsc);
        case 'recvAmount':
          return this.compare(a.recvAmount || 0, b.recvAmount || 0, isAsc);
        case 'currentAmount':
          return this.compare(a.currentAmount || 0, b.currentAmount || 0, isAsc);
        case 'minAmount':
          return this.compare(a.minAmount || 0, b.minAmount || 0, isAsc);
        case 'recvDate':
          return this.compare(a.recvDate || '', b.recvDate || '', isAsc);
        case 'currentDate':
          return this.compare(a.currentDate || '', b.currentDate || '', isAsc);
        case 'rigName':
          return this.compare(a.rigName || '', b.rigName || '', isAsc);
        case 'lastUsedBy':
          return this.compare(a.updateUser || '', b.updateUser || '', isAsc);
        case 'lastUsedIn':
          return this.compare(a.surryName || '', b.surryName || '', isAsc);
        case 'notifyAtReorder':
          return this.compare(a.notifyAtReorder ? 1 : 0, b.notifyAtReorder ? 1 : 0, isAsc);
        case 'trackSample':
          return this.compare(a.trackSample ? 1 : 0, b.trackSample ? 1 : 0, isAsc);
        default:
          return 0;
      }
    });
  }

  private compare(a: number | string, b: number | string, isAsc: boolean): number {
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }

  /**
   * Get material type name from enum
   */
  getMaterialTypeName(type: number): string {
    // MaterialTypes enum mapping
    const types: { [key: number]: string } = {
      1: 'Dry',
      2: 'Liquid',
      3: 'Cement',
      4: 'LiquidBase',
      5: 'Pozolan',
      6: 'LiquidAdditive',
      7: 'SpacerDryBase',
      8: 'SpacerDryFractionBase',
      9: 'SpacerLiquidBase',
      10: 'SpacerLiquidFractionBase',
      11: 'SpacerDryAdditive',
      12: 'SpacerLiquidAdditive'
    };
    return types[type] || 'Unknown';
  }

  /**
   * Format number for print display
   */
  formatAmountForPrint(value: number | null | undefined): string {
    if (value === null || value === undefined) return '';
    return this.decimalPipe.transform(value, '1.6-6') || '';
  }

  /**
   * Format date for print display
   */
  formatDateForPrint(value: string | null | undefined): string {
    if (!value) return '';
    return this.datePipe.transform(value, 'MM/dd/yyyy') || '';
  }

  /**
   * Check if material row should be highlighted (current amount < min amount)
   */
  shouldHighlightRow(material: MaterialsQaqcDto): boolean {
    if (material.currentAmount === null || material.currentAmount === undefined) return false;
    if (material.minAmount === null || material.minAmount === undefined) return false;
    return material.currentAmount < material.minAmount;
  }

  /**
   * Close the dialog (only works when opened as dialog)
   */
  close(): void {
    if (this.dialogRef) {
      this.dialogRef.close();
    }
  }
}
